/**
 * @file 보관함 — 보관된 채널만 모아 보는 별도 화면.
 *
 * <p><b>왜 사이드바가 아니라 별도 화면인가.</b> 보관은 "이제 안 쓴다" 는 뜻이라 평소 목록에 섞이면
 * 사이드바만 길어진다. 그렇다고 아무 데도 안 보이면 삭제와 구분되지 않으므로, 찾아올 수 있는
 * 자리를 하나 만들어 둔다 — 사이드바 맨 아래의 '보관함' 줄이 이 화면으로 온다.
 *
 * <p>행을 누르면 그 채널로 들어간다. 채널 화면은 보관 여부를 보고 스스로 읽기 전용이 되고
 * (배너 + 쓰기 UI 숨김), 채널 관리자에게는 거기서 '보관 해제' 버튼이 뜬다.
 */

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import { channelApi } from "./api/channelApi";
import { notify } from "../../util/toast";
import { serverMsgOr } from "../../util/apiFeedback";
import { fmtDate } from "./dday";
import { ds } from "../../styles/designSystem";

const C = ds.colors;

const TYPE_LABELS = { PROJECT: "프로젝트", OTHER: "기타", NOTICE: "공지(폐지)", TEAM: "팀(폐지)" };

export default function ArchivedChannelsPage() {
    const history = useHistory();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await channelApi.archivedList();
                const list = Array.isArray(data) ? data : (data?.data || data || []);
                if (alive) setRows(list);
            } catch (e) {
                // 목록이 비는 신고는 대개 권한(403) 이라 서버 문구를 살린다 — 채널 목록과 같은 규약.
                if (alive) notify(serverMsgOr("보관함 불러오기 실패")(e?.message), "error");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    return (
        <Box sx={{ flex: 1, height: "100%", overflowY: "auto", bgcolor: C.surface, p: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                <Inventory2RoundedIcon sx={{ fontSize: 22, color: C.textSecondary }} />
                <Typography sx={{ ...ds.typography.pageTitle, fontSize: 18 }}>보관함</Typography>
                <Chip label={rows.length} size="small"
                    sx={{ fontSize: 11, fontWeight: 700, bgcolor: C.borderLight, color: C.textSecondary }} />
                <Box flex={1} />
                <ActionButton variant="secondary"
                    startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 15 }} />}
                    onClick={() => history.push("/channel")}
                    sx={{ fontSize: 12, py: 0.5 }}
                    label="채널로 돌아가기" />
            </Stack>
            {/* 이름 점유를 여기서 밝혀 둔다 — "왜 같은 이름으로 새 채널이 안 만들어지지?" 의 답이
                이 화면에 있어야 사용자가 보관함을 찾아온다(생성 실패 문구도 여기로 안내한다). */}
            <Typography sx={{ fontSize: 12.5, color: C.textMuted, mb: 2.5 }}>
                보관된 채널은 <b>읽기 전용</b>입니다. 내용은 그대로 남아 있고, 채널 관리자가 언제든 되돌릴 수 있습니다.
                <br />
                채널명도 계속 점유하므로 같은 이름으로 새 채널을 만들 수 없습니다 — 이어서 쓰려면 보관을 해제하세요.
            </Typography>

            {loading ? (
                <LoadingDots />
            ) : rows.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8, color: C.textMuted }}>
                    <Inventory2RoundedIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.25 }} />
                    <Typography sx={{ fontWeight: 600, color: C.textSecondary, mb: 0.5 }}>보관된 채널이 없습니다</Typography>
                    <Typography sx={{ fontSize: 13 }}>
                        끝난 채널은 '채널 수정 → 채널 보관'으로 여기에 넣을 수 있습니다
                    </Typography>
                </Box>
            ) : (
                <Table size="small" sx={{ border: `1px solid ${C.border}`, borderRadius: 1 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: C.bg }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5 }}>채널명</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, width: 100 }}>유형</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, width: 180 }}>기간</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, width: 120 }}>보관일</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, width: 110 }}>보관한 사람</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(c => (
                            <TableRow key={c.id} hover
                                onClick={() => history.push(`/channel/${c.id}`)}
                                sx={{ cursor: "pointer" }}>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{ display: "flex", color: C.textMuted }}>
                                            {c.type === "PROJECT"
                                                ? <FolderSpecialRoundedIcon sx={{ fontSize: 16 }} />
                                                : <CategoryRoundedIcon sx={{ fontSize: 16 }} />}
                                        </Box>
                                        <Box minWidth={0}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap>{c.name}</Typography>
                                            {c.description && (
                                                <Typography sx={{ fontSize: 11.5, color: C.textMuted }} noWrap>
                                                    {c.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: C.textSecondary }}>
                                    {TYPE_LABELS[c.type] || c.type}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: C.textSecondary }}>
                                    {c.startDate || c.deadlineDate
                                        ? `${fmtDate(c.startDate) || "…"} ~ ${fmtDate(c.deadlineDate) || "…"}`
                                        : "-"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: C.textSecondary }}>
                                    {fmtDate(c.archivedAt) || "-"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: C.textSecondary }}>
                                    {c.archivedByName || "-"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
